<i18n>
{
	"en": {
		"addOrderTemplates": "Add Order Templates",
		"save": "Save",
		"name": "Name",
		"status": "Status",
		"from": "From",
		"to": "To",
		"customerName": "Customer",
		"nothingToAdd": "There are no more order templates to add",
		"cancel": "Cancel",
		"add": "Add",
		"back": "Back"
	},
	"no": {
		"addOrderTemplates": "Legg Til Oppdragsmaler",
		"save": "Lagre",
		"name": "Navn",
		"status": "Status",
		"from": "Fra",
		"to": "Til",
		"customerName": "Kunde",
		"nothingToAdd": "Det finnes ingen flere oppdragsmaler å legge til",
		"cancel": "Avbryt",
		"add": "Legg til",
		"back": "Tilbake"
	}
}
</i18n>

<template>
	<t-dialog
		:isShown="isShown"
		:title="$t('addOrderTemplates')"
		icon="order"
		width="600px"
		@close="close"
	>
		<div v-show="showGrid">
			<t-grid
				v-if="orderTemplatesNotAdded.length"
				ref="grid"
				:columnDefinitions="columnDefinitions"
				:defaultColumnDefinition="defaultColumnDefinitions"
				:rowHeight="50"
				:items="orderTemplatesNotAdded"
				class="menu-grid"
				selectable
				@selected="onRowsSelected"
			>
				<template #status="{ item }">
					<div>
						<t-timpex-icon
							:icon="getStatusIcon(item)"
							:color="getStatusColor(item)"
							size="md"
						/>
					</div>
				</template>
				<template #despatchParty="{ item }">
					<div style="height: 16px;">{{ item.despatchParty.name }}</div>
					<div class="grid-cell-sub-line">{{ item.despatchParty.street }}, {{ item.despatchParty.postalCode }} {{ item.despatchParty.postalPlace }}</div>
				</template>
				<template #deliveryParty="{ item }">
					<div style="height: 16px;">{{ item.deliveryParty.name }}</div>
					<div class="grid-cell-sub-line">{{ item.deliveryParty.street }}, {{ item.deliveryParty.postalCode }} {{ item.deliveryParty.postalPlace }}</div>
				</template>
				<template #customer="{ item }">
					<div v-if="item.customer" style="height: 16px;">{{ item.customer.name }}</div>
					<div v-if="item.customer" class="grid-cell-sub-line">{{ item.customer.customerNo }}</div>
				</template>
			</t-grid>
			<t-no-content-panel
				v-else
				:text="$t('nothingToAdd')"
				icon="order"
				style="height: 500px"
			/>
		</div>

		<SelectStretches
			v-if="!showGrid"
			ref="stretchSelect"
			:tripTemplate="templateDetails"
			:orderTemplates="selectedOrderTemplates"
			@addToTemplate="addToTemplate"
		/>

		<template #footer>
			<div class="footer">
				<t-button
					@click="isShown = false"
				>{{ $t('cancel') }}</t-button>
				<div class="button-group">
					<t-button
						v-if="!showGrid"
						@click="showGrid = true"
					>{{ $t('back') }}</t-button>
					<t-button
						:disabled="!rowsSelected"
						appearance="primary"
						@click="add"
					>{{ $t('add') }}</t-button>
				</div>
			</div>
		</template>
	</t-dialog>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import {
	eventBus, statuses, goodsTypesIcons, newUUID,
} from '@/core';

import SelectStretches from './SelectStretches';

export default {
	name: 'AddOrderTemplate',
	components: { SelectStretches },
	data() {
		return {
			isShown: false,
			defaultColumnDefinitions: { resizable: true },
			rowsSelected: 0,
			showGrid: true,
			selectedOrderTemplates: []
		};
	},
	computed: {
		...mapState('orderTemplates', ['orderTemplates']),
		...mapState('tripTemplates', ['templateDetails']),
		orderTemplatesNotAdded() {
			return this.orderTemplates.filter(
				({ id }) => this.templateDetails.orderTemplateBindings.every(
					(orderTemplateBinding) => orderTemplateBinding.orderTemplateId !== id
				)
			);
		},
		columnDefinitions() {
			return [
				{
					field: 'name',
					displayName: this.$t('name'),
					valueGetter: ({ name }) => name,
					width: 75
				},
				{
					field: 'status',
					displayName: this.$t('status'),
					valueGetter: ({ status }) => status,
					width: 50,
					center: true,
				},
				{
					field: 'despatchParty',
					displayName: this.$t('from'),
					comparableValueGetter: ({ despatchParty }) => this.addressGetter(despatchParty),
					minWidth: 125,
				},
				{
					field: 'deliveryParty',
					displayName: this.$t('to'),
					comparableValueGetter: ({ deliveryParty }) => this.addressGetter(deliveryParty),
					minWidth: 125,
				},
				{
					field: 'customer',
					displayName: this.$t('customerName'),
					comparableValueGetter: ({ customer }) => customer ? `${customer.name} ${customer.customerNo}` : '',
					minWidth: 125,
					type: 'customer',
				},
			];
		}
	},
	created() {
		eventBus.$on('addOrderTemplate', this.setShown);
	},
	methods: {
		...mapActions('tripTemplates', ['setOrderTemplateBindings']),
		onRowsSelected(ids) {
			this.rowsSelected = ids.length;
		},
		getStatusIcon({ goodsType }) {
			return goodsTypesIcons(goodsType);
		},
		getStatusColor({ status }) {
			return statuses.color(status);
		},
		addressGetter(party) {
			if (!party) return '';
			return `${party.name} ${party.street} ${party.postalCode} ${party.postalPlace}`;
		},
		setShown(value) {
			this.isShown = value;
		},
		add() {
			if (this.showGrid) {
				this.selectedOrderTemplates = this.$refs.grid.getSelectedItems();
				this.showGrid = false;
			} else {
				this.$refs.stretchSelect.addToTemplate();
				this.isShown = false;
			}
		},
		close() {
			this.isShown = false;
			this.showGrid = true;
		},
		/* eslint-disable-next-line */
		async addToTemplate(data) {
			// TODO: Add map orderTemplates to tripOrders
			// await this.addOrdersToTrip({
			// 	orderTemplates: this.selectedOrderTemplates,
			// 	tripTemplateId: this.templateDetails.id,
			// 	...data
			// });

			let selectedOrderTemplateBindings = this.selectedOrderTemplates.map(
				orderTemplate => ({
					id: newUUID(),
					orderTemplate,
					orderTemplateId: orderTemplate.id,
					tripTemplateId: this.templateDetails.id,
				})
			);

			this.setOrderTemplateBindings([ ...this.templateDetails.orderTemplateBindings, ...selectedOrderTemplateBindings ]);
		}
	}
};
</script>

<style lang="scss" scoped>

.menu-grid {
	height: 400px;
	width: 100%;
}

.footer {
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
}
.button-group {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	> * {
		margin-left: 10px;
	}
}
</style>